// Cached fetch of /Indicators. Module-level cache so navigating away and back
// doesn't re-fetch; the catalog is static for the lifetime of the page.

import { useEffect, useState } from "react";
import { fetchIndicatorCatalog, type IndicatorCatalogEntry } from "../api/indicators";

let cachedCatalog: IndicatorCatalogEntry[] | null = null;
let inflightFetch: Promise<IndicatorCatalogEntry[]> | null = null;

export interface IndicatorCatalogState {
  catalog: IndicatorCatalogEntry[];
  loading: boolean;
  error: string | null;
}

export function useIndicatorCatalog(): IndicatorCatalogState {
  const [state, setState] = useState<IndicatorCatalogState>(() => ({
    catalog: cachedCatalog ?? [],
    loading: cachedCatalog === null,
    error: null,
  }));

  // Bumped after a failed fetch to auto-retry while mounted, so a transient blip self-heals
  // without forcing the user to navigate away and back (the old behavior).
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    if (cachedCatalog !== null) return;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    setState((s) => (s.error ? { ...s, loading: true, error: null } : s));
    inflightFetch ??= fetchIndicatorCatalog();
    inflightFetch
      .then((result) => {
        cachedCatalog = result;
        if (!cancelled) setState({ catalog: result, loading: false, error: null });
      })
      .catch((err: Error) => {
        inflightFetch = null;
        if (!cancelled) {
          setState({ catalog: [], loading: false, error: err.message });
          retryTimer = setTimeout(() => setRetryTick((t) => t + 1), 4000);
        }
      });
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [retryTick]);

  return state;
}
